import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Cpu, BarChart3, Clock, Package, DollarSign,
  CheckCircle2, AlertTriangle, XCircle, Zap, FileText,
  GitMerge, ChevronDown, ChevronUp, Info, Award,
  Mail, Upload, Brain, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

// Accordion item
function AccordionCard({ icon: Icon, iconColor, iconBg, title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <motion.div
      className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden"
      layout
      transition={{ duration: 0.2 }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div>
            <p className="font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk' }}>{title}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="px-6 pb-6"
        >
          <Separator className="mb-5" />
          {children}
        </motion.div>
      )}
    </motion.div>
  );
}

// Rule card
function RuleCard({ grade, color, bg, border, icon: Icon, label, description, detail }) {
  return (
    <div className={`rounded-xl border ${border} ${bg} p-4 flex gap-4`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color === 'emerald' ? 'bg-emerald-100' : color === 'amber' ? 'bg-amber-100' : color === 'red' ? 'bg-red-100' : 'bg-zinc-200'}`}>
        <Icon className={`w-4.5 h-4.5 ${color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : color === 'red' ? 'text-red-500' : 'text-zinc-700'}`}
          style={{ width: 18, height: 18 }} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            color === 'emerald' ? 'bg-emerald-100 text-emerald-800'
            : color === 'amber' ? 'bg-amber-100 text-amber-800'
            : color === 'red' ? 'bg-red-100 text-red-800'
            : 'bg-zinc-200 text-zinc-800'
          }`}>{grade}</span>
          <span className="text-sm font-semibold text-foreground">{label}</span>
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
        {detail && <p className="text-xs text-foreground/70 mt-1 font-medium">{detail}</p>}
      </div>
    </div>
  );
}

// Pipeline step
function PipelineStep({ step, icon: Icon, iconColor, iconBg, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${iconBg} ${iconColor}`}
          style={{ minWidth: 36 }}>
          {step}
        </div>
        {step < 5 && <div className="w-0.5 h-full min-h-[24px] bg-border mt-1" />}
      </div>
      <div className="pb-6">
        <div className="flex items-center gap-2 mb-0.5">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="page-pad max-w-[900px]">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary-foreground" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Settings & Reference
          </h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1 ml-[52px]">
          Audit rules, AI model details, and how the pipeline works.
        </p>
      </div>

      <div className="space-y-4">

        {/* ── AI MODEL ── */}
        <AccordionCard
          icon={Brain}
          iconColor="text-violet-600"
          iconBg="bg-violet-100"
          title="AI Model — Claude Sonnet 4.6"
          subtitle="AWS Bedrock · eu-west-3 · Extended Thinking enabled"
          defaultOpen
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              { label: 'Model',        value: 'claude-sonnet-4-6',    icon: Cpu },
              { label: 'Provider',     value: 'AWS Bedrock (Anthropic)', icon: Shield },
              { label: 'Region',       value: 'eu-west-3 (Paris)',      icon: Info },
              { label: 'Inference',    value: 'eu.anthropic.claude-sonnet-4-6', icon: Zap },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-start gap-3 p-3.5 rounded-xl bg-secondary/50 border border-border/60">
                <Icon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-medium font-mono text-foreground mt-0.5">{value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-violet-50 border border-violet-100 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-3 flex items-center gap-1.5">
              <Brain className="w-3.5 h-3.5" />Extended Thinking (Reasoning Mode)
            </p>
            <p className="text-xs text-violet-800 leading-relaxed">
              Claude Sonnet 4.6 supports <strong>extended thinking</strong> — before each action the model
              reasons through its approach using a private reasoning chain (up to 2,000 tokens).
              This gives significantly better accuracy on complex multi-SKU cross-referencing
              and nuanced audit decisions. You can view the reasoning in the <strong>Agent Log</strong> tab
              on any audit result.
            </p>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Context Window', value: '200K tokens', desc: 'Handles large inventories' },
              { label: 'Output Tokens',  value: '12,000',      desc: 'Per audit draft call' },
              { label: 'Thinking Budget',value: '2,000 tokens',desc: 'Per agent iteration' },
            ].map(({ label, value, desc }) => (
              <div key={label} className="text-center p-3 rounded-xl border border-border/60 bg-white">
                <p className="text-xl font-bold text-primary" style={{ fontFamily: 'Space Grotesk' }}>{value}</p>
                <p className="text-xs font-semibold text-foreground mt-0.5">{label}</p>
                <p className="text-[11px] text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </AccordionCard>

        {/* ── AUDIT RULES ── */}
        <AccordionCard
          icon={BarChart3}
          iconColor="text-primary"
          iconBg="bg-primary/10"
          title="Audit Rules"
          subtitle="Three deterministic checks applied to every SKU"
          defaultOpen
        >
          {/* Rule 1: WOS */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold">1. WOS Classification (Weeks of Stock)</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-1">Formula: <span className="font-mono bg-secondary px-1.5 py-0.5 rounded">WOS = Current Stock ÷ (Sales Last 4 Weeks ÷ 4)</span></p>
            <p className="text-xs text-muted-foreground mb-3">Measures how many weeks of inventory you have at the current sales rate.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <RuleCard grade="OK" color="emerald" border="border-emerald-100" icon={CheckCircle2}
                label="Low Risk" description="WOS ≤ 8 weeks" detail="Inventory moving well. Safe to reorder." />
              <RuleCard grade="FLAG" color="red" border="border-red-100" icon={XCircle}
                label="High Risk" description="WOS > 8 weeks" detail="Excess inventory. Avoid reordering unless critical." />
              <RuleCard grade="STAGNANT" color="zinc" border="border-zinc-200" icon={XCircle}
                label="Critical Risk" description="0 sales (4w) + stock aged >60 days" detail="Auto Red Flag. Do not reorder." />
            </div>
          </div>

          <Separator className="my-5" />

          {/* Rule 2: Ageing */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold">2. Ageing Inventory Rule (Portfolio Gate)</h3>
            </div>
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">🚩 Reorder Blocked Condition</p>
              <p className="text-sm text-red-800">
                If <strong>more than 10% of total inventory units</strong> have been in stock
                for <strong>more than 60 days</strong>, the <strong>entire reorder is blocked</strong> and
                every PI item is flagged as HIGH ALERT.
              </p>
              <p className="text-xs text-red-700 mt-2">
                Example: 1,500 total stock units, 200 units &gt;60 days old = <strong>13.3%</strong> → 🚩 Reorder blocked
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Note: Ageing is now portfolio-level — per-SKU ageing % is shown in the table for context but does not flag individual items.
              If not available, this check is skipped and shown as a warning.
            </p>
          </div>

          <Separator className="my-5" />

          {/* Rule 3: Price Integrity */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold">3. PI Price Integrity Check</h3>
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
              <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-2">🚩 Price Flag Condition</p>
              <p className="text-sm text-orange-800">
                If the <strong>Proforma Invoice (PI) price</strong> is <strong>higher by any amount</strong> than
                the <strong>Last Sold Price</strong>, a price flag is triggered.
              </p>
              <p className="text-xs text-orange-700 mt-2">
                Example: PI Price = $85.00, Last Sold = $79.99 → $5.01 increase → 🚩 Price Flag triggered
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              If the document does not contain a Last Sold Price column, this check is skipped.
            </p>
          </div>
        </AccordionCard>

        {/* ── RECOMMENDATION SCALE ── */}
        <AccordionCard
          icon={Award}
          iconColor="text-emerald-600"
          iconBg="bg-emerald-100"
          title="Recommendation Scale"
          subtitle="ALLOW / ALERT / HIGH ALERT — how decisions are made"
        >
          <div className="space-y-3">
            {[
              {
                rec: 'ALLOW', color: 'emerald',
                icon: CheckCircle2,
                rule: 'No flags detected across all 3 checks.',
                action: 'Safe to approve the reorder.',
              },
              {
                rec: 'ALERT', color: 'orange',
                icon: AlertTriangle,
                rule: 'High WOS (>8 weeks) without a price flag, or a price-only flag.',
                action: 'Manual review required before approving.',
              },
              {
                rec: 'HIGH ALERT', color: 'red',
                icon: XCircle,
                rule: 'STAGNANT inventory, or HIGH WOS combined with a Price flag, or PORTFOLIO ageing >10% (entire reorder is blocked).',
                action: 'Do not reorder. Address root cause first.',
              },
            ].map(({ rec, color, icon: Icon, rule, action }) => (
              <div key={rec} className={`rounded-xl border p-4 flex gap-4 ${
                color === 'emerald' ? 'border-emerald-200 bg-emerald-50'
                : color === 'orange'  ? 'border-orange-200 bg-orange-50'
                : 'border-red-200 bg-red-50'
              }`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  color === 'emerald' ? 'bg-emerald-100' : color === 'orange' ? 'bg-orange-100' : 'bg-red-100'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    color === 'emerald' ? 'text-emerald-600' : color === 'orange' ? 'text-orange-600' : 'text-red-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-bold ${
                      color === 'emerald' ? 'text-emerald-700' : color === 'amber' ? 'text-amber-700' : 'text-red-700'
                    }`}>{rec}</span>
                  </div>
                  <p className="text-xs text-foreground/80 mb-1"><span className="font-semibold">When:</span> {rule}</p>
                  <p className="text-xs text-foreground/70"><span className="font-semibold">Action:</span> {action}</p>
                </div>
              </div>
            ))}
          </div>
        </AccordionCard>

        {/* ── HOW AUDIT WORKS ── */}
        <AccordionCard
          icon={Zap}
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
          title="How the Audit Pipeline Works"
          subtitle="5 automated steps from input to decision"
        >
          <div className="space-y-0">
            {[
              {
                step: 1, icon: Upload, iconBg: 'bg-blue-100', iconColor: 'text-blue-600',
                title: 'Input Ingestion',
                desc: 'You provide an email body (any format: pasted text, tab-separated table, narrative) and/or file attachments (Excel, CSV, PDF). Files are parsed in parallel using a thread pool for speed.',
              },
              {
                step: 2, icon: FileText, iconBg: 'bg-violet-100', iconColor: 'text-violet-600',
                title: 'Data Extraction',
                desc: 'The Universal Parser detects product tables (any delimiter: tabs, spaces) and maps columns automatically. For PDFs and unstructured text, Claude extracts structured data via LLM. For Gmail forwarded emails, a specialised multi-chunk extraction handles reformatted text.',
              },
              {
                step: 3, icon: GitMerge, iconBg: 'bg-orange-100', iconColor: 'text-orange-600',
                title: 'Cross-Referencing',
                desc: 'If both email data and document data exist, they are merged. Email PI prices take priority (it\'s the order request). Document figures are authoritative for stock/sales numbers. Discrepancies > 5% are flagged and shown in the Agent Log.',
              },
              {
                step: 4, icon: BarChart3, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600',
                title: 'Rules Engine (Deterministic)',
                desc: 'WOS, Ageing, and Price Integrity checks run in pure Python — no AI involved. These are deterministic calculations that always produce the same result for the same inputs. Fast (< 100ms for 100 SKUs).',
              },
              {
                step: 5, icon: Mail, iconBg: 'bg-primary/10', iconColor: 'text-primary',
                title: 'AI Report Generation',
                desc: 'Claude Sonnet 4.6 writes a professional audit email draft summarising all findings, flagged SKUs, and recommendations. The AI\'s reasoning is visible in the Agent Log. The draft is ready to copy, download, or forward.',
              },
            ].map(s => <PipelineStep key={s.step} {...s} />)}
          </div>
        </AccordionCard>

        {/* ── SUPPORTED FORMATS ── */}
        <AccordionCard
          icon={FileText}
          iconColor="text-slate-600"
          iconBg="bg-slate-100"
          title="Supported File Formats"
          subtitle="What you can attach and how each is handled"
        >
          <div className="space-y-3">
            {[
              {
                format: 'CSV (.csv)',
                icon: '📄',
                how: 'Parsed directly in Python. Column names auto-detected (model, stock, sales, price, etc.).',
                best: 'Inventory exports from your ERP/WMS.',
              },
              {
                format: 'Excel (.xlsx / .xls)',
                icon: '📊',
                how: 'Loaded sheet-by-sheet. Automatically finds the product table header row (even if there are summary rows at the top, like Mitsumi\'s WEEK OF STOCK format). Supports chunking for 150+ row sheets.',
                best: 'Weekly inventory/order reports with MARKETING NAME + ITEM_CODE structure.',
              },
              {
                format: 'PDF (.pdf)',
                icon: '📑',
                how: 'Text extracted with pdfplumber. If structured tables are found, parsed directly. Otherwise, Claude LLM extracts product data from the text. Multiple PDFs are batched into one extraction call.',
                best: 'Proforma invoices (PI prices), purchase orders.',
              },
            ].map(({ format, icon, how, best }) => (
              <div key={format} className="rounded-xl border border-border/60 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{icon}</span>
                  <span className="font-semibold text-sm font-mono">{format}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-1"><span className="font-semibold text-foreground">How it's parsed:</span> {how}</p>
                <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Best for:</span> {best}</p>
              </div>
            ))}
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-800">
              ⚠️ <strong>Image-only PDFs</strong> (scanned documents) cannot be read without OCR. Please provide
              text-based PDFs or Excel files for best results.
            </div>
          </div>
        </AccordionCard>

      </div>
    </div>
  );
}
